import { defineMessages } from 'react-intl';

// Ids are formatjs content hashes (`[sha512:contenthash:base64:10]`, enforced
// by `formatjs/enforce-id`); `eslint --fix` regenerates them after a copy edit.
// `<b>` and `<link>` are rich-text tags the dialog renders as bold text and the
// manual-download link.
export const desktopDownloadMessages = defineMessages({
  startingTitle: {
    defaultMessage: 'Downloading Heidi',
    description:
      'Title of the Dictate install-help dialog after the app asked the browser to download the installer. It does not claim the download finished.',
    id: '+1t2XMRyB2',
  },
  startingDescription: {
    defaultMessage:
      'Your download will start automatically. If it didn’t start, <link>download manually.</link>',
    description:
      'Description under the Dictate install-help title. <link> downloads the same installer again.',
    id: 'Dw0gsv4fZh',
  },
  failedTitle: {
    defaultMessage: 'Start your Heidi download',
    description:
      'Title of the Dictate install-help dialog when the automatic installer download could not be started',
    id: 'dogwOIXIL0',
  },
  failedDescription: {
    defaultMessage:
      'Your download didn’t start automatically. <link>Download manually.</link>',
    description:
      'Description when the automatic Dictate download could not start. <link> downloads the installer.',
    id: 'YNbwBq64Ed',
  },
  unsupportedTitle: {
    defaultMessage: 'Download Heidi on a computer',
    description:
      'Title of the Dictate install-help dialog on a phone, tablet or unsupported computer',
    id: 'gKSBqWr3r6',
  },
  unsupportedDescription: {
    defaultMessage: 'Dictate for desktop is available on macOS and Windows.',
    description: 'Explains which systems the Dictate desktop app supports',
    id: 'ef+bIywCzE',
  },
  unsupportedLinksHeading: {
    defaultMessage: 'Download for another computer',
    description:
      'Heading above the manual Mac and Windows installer links on an unsupported device',
    id: '8/SblLi2g3',
  },
  stepsLabel: {
    defaultMessage: 'Installation steps',
    description: 'Accessible label for the ordered list of installation steps',
    id: 'UQOaY2ioy1',
  },
  macStepOpenDmg: {
    defaultMessage: 'Open <b>Heidi.dmg</b> from your downloads',
    description: 'Mac installation step 1',
    id: 'EftiohWBJg',
  },
  macStepDragToApplications: {
    defaultMessage: 'Drag the <b>Heidi icon</b> into your <b>Applications</b> folder.',
    description: 'Mac installation step 2',
    id: 'oV1+qjB8Sj',
  },
  macStepOpenHeidi: {
    defaultMessage: 'Open the <b>Heidi app</b> from your <b>Applications</b> folder.',
    description: 'Mac installation step 3',
    id: 'y0o9KhH/et',
  },
  windowsStepOpenInstaller: {
    // Figma 6071:37226 says "Heidi.dmg" here, carried over from the Mac frame;
    // Windows users get an .exe or .msi.
    defaultMessage: 'Open <b>{fileName}</b> from your downloads',
    description:
      'Windows installation step 1; fileName is the installer, e.g. Heidi-setup.exe',
    id: 'tH9xYpviVP',
  },
  windowsStepSetupWizard: {
    defaultMessage: 'Complete the steps in the Setup wizard',
    description: 'Windows installation step 2: finish the Heidi installer',
    id: '8ZsSJTPuG5',
  },
  windowsStepPinToTaskbar: {
    defaultMessage: 'Drag Heidi to your Taskbar to pin it',
    description: 'Windows installation step 3: pin the Heidi app to the Windows taskbar',
    id: 'PzD1LVMvXj',
  },
  changeVersion: {
    defaultMessage: 'Change version',
    description:
      'Button in the Dictate install-help dialog that opens the choice of installer (Apple Silicon or Intel, .exe or .msi)',
    id: 'y21B51oQTk',
  },
  back: {
    defaultMessage: 'Back',
    description:
      'Button that leaves the installer choice and returns to the installation steps',
    id: 'fAApH0ZhcM',
  },
  chooseMacTitle: {
    defaultMessage: 'Choose your Mac version',
    description: 'Title of the installer choice on a Mac',
    id: '8dWTLRKo0p',
  },
  chooseMacDescription: {
    defaultMessage:
      'Open the Apple menu and choose About This Mac. If Chip shows Apple M1 or later, pick Apple Silicon. If Processor shows Intel, pick Intel.',
    description: 'Explains how to tell an Apple Silicon Mac from an Intel Mac',
    id: 'z/e9UNf1fk',
  },
  chooseWindowsTitle: {
    defaultMessage: 'Choose your Windows installer',
    description: 'Title of the installer choice on Windows',
    id: 'eXLyycOOYU',
  },
  chooseWindowsDescription: {
    defaultMessage:
      'Most people should pick the .exe installer. Pick the MSI installer if your IT team deploys apps for you.',
    description: 'Explains the difference between the Windows .exe and .msi installers',
    id: 'ptSxziTgIE',
  },
  appleSilicon: {
    defaultMessage: 'Apple Silicon',
    description: 'Installer choice: Mac with an Apple Silicon (M-series) chip',
    id: '3lvAlXztzx',
  },
  intel: {
    defaultMessage: 'Intel',
    description: 'Installer choice: Mac with an Intel chip',
    id: '3DBhz2irLR',
  },
  windowsExe: {
    defaultMessage: '.exe installer',
    description: 'Installer choice: the standard Windows .exe installer',
    id: 'iIJqkAkso2',
  },
  windowsMsi: {
    defaultMessage: 'MSI installer',
    description: 'Installer choice: the Windows .msi installer for IT-managed devices',
    id: 'M7Skgu/mxI',
  },
  macAppleSiliconLink: {
    defaultMessage: 'Mac with Apple Silicon',
    description: 'Manual installer link on an unsupported device: Apple Silicon Mac',
    id: 'M4VF3MfXxH',
  },
  macIntelLink: {
    defaultMessage: 'Mac with Intel',
    description: 'Manual installer link on an unsupported device: Intel Mac',
    id: 'WqrVMTX0/L',
  },
  windowsLink: {
    defaultMessage: 'Windows',
    description: 'Manual installer link on an unsupported device: Windows .exe',
    id: 'u5PrR1hwZ4',
  },
});
